// pages/calendar/eventDetails/eventDetails.js
import React, { useEffect, useReducer, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, DatePicker, TimePicker, Checkbox, Button, Modal } from "antd";
import { FaPlus, FaEdit, FaTrash, FaCopy } from "react-icons/fa";
import dayjs from "dayjs";
import axios from "../../../services/axiosConfig";
import Layout from "../../../components/layout/layout";
import useNotification from "../../../hooks/useNotification";
import "../../../index.css";
import "./eventDetails.css";
import { getAuthToken } from "../../../auth/auth";
import API_ENDPOINTS from "../../../services/apiEndpoints";

const { TextArea } = Input;

const initialState = {
  imageSrc: null,
  initialImageSrc: null,
  showConfirmDialog: false,
  isDeleting: false,
  initialValues: {},
  formValues: {},
  links: [{ id: 0, value: "" }],
  copyTooltipIndex: null,
  deleteMode: "SINGLE",
};

const eventReducer = (state, action) => {
  switch (action.type) {
    case 'setImageSrc':
      return { ...state, imageSrc: action.payload };
    case 'setInitialImageSrc':
      return { ...state, initialImageSrc: action.payload };
    case 'setShowConfirmDialog':
      return { ...state, showConfirmDialog: action.payload };
    case 'setIsDeleting':
      return { ...state, isDeleting: action.payload };
    case 'setInitialValues':
      return { ...state, initialValues: action.payload };
    case 'setFormValues':
      return { ...state, formValues: action.payload };
    case 'setLinks':
      return { ...state, links: action.payload };
    case 'setCopyTooltipIndex':
      return { ...state, copyTooltipIndex: action.payload };
    case 'resetImage':
      return { ...state, imageSrc: null };
    case 'setDeleteMode':
      return { ...state, deleteMode: action.payload };
    default:
      return state;
  }
};

function EventDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const event = location.state;
  const isEditMode = Boolean(event);

  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(eventReducer, initialState);
  const fileInputRef = useRef(null);

  const {
    imageSrc,
    initialImageSrc,
    showConfirmDialog,
    isDeleting,
    initialValues,
    formValues,
    links,
    copyTooltipIndex,
    deleteMode,
  } = state;

  const convertToKolkata = useCallback((value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;

    const datePart = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);

    const timePart = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    return { datePart, timePart };
  }, []);

  const convert24To12 = useCallback((time24) => {
    let [hours, minutes] = time24.split(":").map(Number);
    let period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    return `${hours}:${minutes} ${period}`;
  }, []);

  const handleAddEditClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      dispatch({ type: 'setImageSrc', payload: imageURL });
    }
  }, []);

  const handleDeleteClick = useCallback(() => {
    dispatch({ type: 'resetImage' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleAddLink = useCallback(() => {
    const newId = links.length > 0 ? Math.max(...links.map(l => l.id)) + 1 : 0;
    dispatch({ type: 'setLinks', payload: [...links, { id: newId, value: "" }] });
  }, [links]);

  const handleLinkChange = useCallback((id, value) => {
    dispatch({
      type: 'setLinks',
      payload: links.map(link => link.id === id ? { ...link, value } : link),
    });
  }, [links]);

  const handleDeleteLink = useCallback((id) => {
    if (links.length > 1) {
      dispatch({
        type: 'setLinks',
        payload: links.filter(link => link.id !== id),
      });
    }
  }, [links]);

  const handleCopyLink = useCallback(async (linkValue, index) => {
    if (!linkValue || linkValue.trim() === '') return;

    try {
      await navigator.clipboard.writeText(linkValue);
      dispatch({ type: 'setCopyTooltipIndex', payload: index });
      setTimeout(() => {
        dispatch({ type: 'setCopyTooltipIndex', payload: null });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showNotification('Failed to copy link', 'error');
    }
  }, [showNotification]);

  const isFormModified = useCallback(() => {
    const currentValues = form.getFieldsValue();

    return Object.keys(currentValues).some(key => {
      const current = currentValues[key];
      const initial = initialValues[key];

      if (current && current._isADayJSObject && initial && initial._isADayJSObject) {
        return !current.isSame(initial);
      }

      return current !== initial;
    }) || imageSrc !== initialImageSrc;
  }, [form, initialValues, imageSrc, initialImageSrc]);

  const isSaveButtonEnabled = useMemo(() => {
    if (isEditMode) {
      return isFormModified();
    } else {
      const hasTitle = formValues.title?.trim();
      const hasStartDate = formValues.startDate !== null && formValues.startDate !== undefined;
      const hasStartTime = formValues.startTime !== null && formValues.startTime !== undefined;
      const hasEndTime = formValues.endTime !== null && formValues.endTime !== undefined;
      const hasVenueDetails = formValues.venueDetails?.trim();

      return hasTitle && hasStartDate && hasStartTime && hasEndTime && hasVenueDetails;
    }
  }, [isEditMode, isFormModified, formValues]);

  const handleConfirmDelete = useCallback(async () => {
    dispatch({ type: 'setIsDeleting', payload: true });
    const title = form.getFieldValue('title');

    try {
      const token = await getAuthToken();
      const occurrenceDate = dayjs(event?.start).format('YYYY-MM-DD');

      // FIX 2: Use event.extendedProps.id if available, else fall back to event.id
      // This ensures newly created events (whose id is at event.id) can also be deleted.
      const eventId = event?.extendedProps?.id ?? event?.id;

      const res = await axios.delete(API_ENDPOINTS.CALENDAR, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          id: eventId,
          title: title,
          deleteType: deleteMode,
          ...(deleteMode === "SINGLE" && { occurrenceDate }),
        },
      });
      console.log("DELETE MODE:", deleteMode);
      console.log("Delete response:", res);
      showNotification(`Event "${title}" deleted successfully.`, 'success');
      dispatch({ type: 'setShowConfirmDialog', payload: false });
      navigate("/eventList");
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(
        "Failed to delete event: " + (error.response?.data?.message || error.message),
        'error'
      );
    } finally {
      dispatch({ type: 'setIsDeleting', payload: false });
    }
  }, [form, event, navigate, deleteMode, showNotification]);

  const handleSave = useCallback(async () => {
    try {
      await form.validateFields(['title', 'startDate', 'startTime', 'endTime', 'venueDetails']);

      const values = form.getFieldsValue();
      const startDate = values.startDate.format('YYYY-MM-DD');
      const startTime = values.startTime.format('HH:mm');
      const endTime = values.endTime.format('HH:mm');

      // FIX 1: Extract endDate from form values.
      // If endDate is not provided, fall back to startDate (single-day event).
      const endDate = values.endDate
        ? values.endDate.format('YYYY-MM-DD')
        : startDate;

      const validLinks = links
        .filter(link => link.value.trim() !== "")
        .map(link => link.value);

      const payload = {
        ...(isEditMode && { id: event?.extendedProps?.id }),
        title: values.title,
        description: values.description || "",
        date: startDate,
        endDate: endDate,        // FIX 1: endDate is now included in the payload
        timings: `${convert24To12(startTime)} to ${convert24To12(endTime)}`,
        venue: values.venueDetails,
        recurring: values.recurring || false,
        links: validLinks,
      };

      const token = await getAuthToken();
      const url = API_ENDPOINTS.CALENDAR;
      const method = isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // FIX 2: Parse the server response to capture the newly created event's id.
        // The event list must receive this id so that delete works immediately after creation.
        const responseData = await res.json().catch(() => null);
        const newEventId = responseData?.id ?? responseData?.data?.id ?? null;

        if (!isEditMode && newEventId) {
          // Store the new id so the event list can attach it to the event's extendedProps.
          // How you pass this depends on your event list implementation — common approaches:
          //   1. navigate("/eventList", { state: { newEventId } }) and handle it in the list.
          //   2. Let the event list refetch from the server (recommended — always up to date).
          console.log("Newly created event id:", newEventId);
        }

        showNotification(
          isEditMode ? "Event updated successfully!" : "Event created successfully!",
          'success'
        );
        navigate("/eventList");
      } else {
        const errorData = await res.json().catch(() => null);
        const detail = errorData?.message || res.statusText || "Unknown error";
        showNotification(`Error ${res.status}: ${detail}`, 'error');
      }
    } catch (error) {
      if (error.errorFields) {
        showNotification("Please fill in all required fields", 'error');
      } else {
        showNotification(`Network error: ${error.message}`, 'error');
      }
    }
  }, [form, links, convert24To12, navigate, isEditMode, event, showNotification]);

  const handleCancel = useCallback(() => {
    navigate("/eventList");
  }, [navigate]);

  const handleValuesChange = useCallback((_, allValues) => {
    dispatch({ type: 'setFormValues', payload: allValues });
  }, []);

  useEffect(() => {
    if (event) {
      console.log("Received event data:", event);

      const startDateTime = event?.start;
      const endDateTime = event?.end;

      const start = convertToKolkata(startDateTime);
      const end = convertToKolkata(endDateTime || startDateTime);

      const formValues = {
        title: event?.title || "",
        description: event?.extendedProps?.description || "",
        startDate: start ? dayjs(start.datePart) : null,
        startTime: start ? dayjs(start.timePart, "HH:mm") : null,
        endDate: end ? dayjs(end.datePart) : null,
        endTime: end ? dayjs(end.timePart, "HH:mm") : null,
        venueDetails: event?.extendedProps?.venue || "",
        recurring: event?.extendedProps?.recurring || false,
      };

      form.setFieldsValue(formValues);
      dispatch({ type: 'setInitialValues', payload: formValues });
      dispatch({ type: 'setInitialImageSrc', payload: null });

      const eventLinks = event?.extendedProps?.links || [];
      if (eventLinks.length > 0) {
        dispatch({
          type: 'setLinks',
          payload: eventLinks.map((link, idx) => ({ id: idx, value: link })),
        });
      }
    } else {
      const emptyValues = {
        title: "",
        description: "",
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        venueDetails: "",
        recurring: false,
      };
      form.setFieldsValue(emptyValues);
      dispatch({ type: 'setInitialValues', payload: emptyValues });
      dispatch({ type: 'setInitialImageSrc', payload: null });
    }
  }, [event, form, convertToKolkata]);

  const deleteModalTitle = useMemo(() => form.getFieldValue('title'), [form]);

  return (
    <Layout
      navTitle="Calendar & Events"
      notificationProps={{
        show: notification.show,
        message: notification.message,
        type: notification.type,
        onClose: hideNotification,
        duration: 4000
      }}
    >
      <div className="evd-form-container">
        <div className="evd-form-wrapper">
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
            className="evd-antd-form"
          >
            <div className="evd-form-section">
              <h3 className="evd-section-title">Event Details</h3>

              <Form.Item
                label="Event Title"
                name="title"
                rules={[{ required: true, message: 'Please enter an event title' }]}
              >
                <Input placeholder="Enter event title" />
              </Form.Item>

              <Form.Item name="recurring" valuePropName="checked">
                <Checkbox>Weekly Meeting</Checkbox>
              </Form.Item>
            </div>

            <div className="evd-form-section">
              <h3 className="evd-section-title">Date & Time</h3>
              <div className="evd-form-row">
                <Form.Item
                  label="Start Date"
                  name="startDate"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item
                  label="Start Time"
                  name="startTime"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <TimePicker style={{ width: '100%' }} format="HH:mm" />
                </Form.Item>
              </div>

              <div className="evd-form-row">
                <Form.Item label="End Date" name="endDate">
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item
                  label="End Time"
                  name="endTime"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <TimePicker style={{ width: '100%' }} format="HH:mm" />
                </Form.Item>
              </div>
            </div>

            <div className="evd-form-section">
              <h3 className="evd-section-title">Location & Description</h3>

              <Form.Item
                label="Venue Details"
                name="venueDetails"
                rules={[{ required: true, message: 'Please enter venue details' }]}
              >
                <TextArea rows={3} placeholder="Enter venue details" />
              </Form.Item>

              <Form.Item label="Event Description" name="description">
                <TextArea rows={5} placeholder="Enter event description" />
              </Form.Item>
            </div>

            <div className="evd-form-section">
              <h3 className="evd-section-title">Event Links</h3>
              <div className="evd-links-header">
                <Button
                  type="dashed"
                  icon={<FaPlus />}
                  onClick={handleAddLink}
                  size="small"
                >
                  Add Link
                </Button>
              </div>

              {links.map((link, index) => (
                <div key={link.id} className="evd-links-row">
                  <Input
                    placeholder="Zoom Link / Google Meet Link"
                    type="url"
                    value={link.value}
                    onChange={(e) => handleLinkChange(link.id, e.target.value)}
                    className="evd-link-input"
                  />
                  <div className="evd-link-actions">
                    <div style={{ position: 'relative' }}>
                      <Button
                        icon={<FaCopy />}
                        onClick={() => handleCopyLink(link.value, index)}
                        disabled={!link.value.trim()}
                        size="small"
                      />
                      {copyTooltipIndex === index && (
                        <div className="evd-copy-tooltip">
                          Copied!
                          <div className="evd-copy-tooltip-arrow" />
                        </div>
                      )}
                    </div>
                    {links.length > 1 && (
                      <Button
                        icon={<FaTrash />}
                        onClick={() => handleDeleteLink(link.id)}
                        size="small"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="evd-form-section">
              <h3 className="evd-section-title">Event Image</h3>
              <div className="evd-image-holder">
                {imageSrc ? (
                  <img src={imageSrc} alt="Event" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                ) : (
                  <p style={{ color: 'black' }}>No image selected</p>
                )}
              </div>
              <div className="evd-image-btns">
                {!isEditMode && (
                  <button onClick={handleAddEditClick}><FaPlus /></button>
                )}
                {isEditMode && (
                  <button onClick={handleAddEditClick}><FaEdit /></button>
                )}
                <button onClick={handleDeleteClick}><FaTrash /></button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </div>

            <div className="evd-form-btn-group">
              <Button className="evd-form-cancel-btn" onClick={handleCancel}>
                Cancel
              </Button>

              {isEditMode && (
                <Button
                  className="evd-form-delete-btn"
                  onClick={() => dispatch({ type: 'setShowConfirmDialog', payload: true })}
                >
                  Delete
                </Button>
              )}

              <Button
                className="evd-form-save-btn"
                onClick={handleSave}
                disabled={!isSaveButtonEnabled}
              >
                {isEditMode ? 'Update' : 'Save'}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <Modal
        title="Confirm Delete"
        open={showConfirmDialog}
        onOk={handleConfirmDelete}
        onCancel={() => dispatch({ type: 'setShowConfirmDialog', payload: false })}
        okText={isDeleting ? "Deleting..." : "Yes"}
        cancelText="No"
        okButtonProps={{ danger: true, disabled: isDeleting }}
        cancelButtonProps={{ disabled: isDeleting }}
      >
        <p>Are you sure you want to delete this event?</p>
        <p><strong>"{deleteModalTitle}"</strong></p>

        {event?.extendedProps?.recurring && (
          <div style={{ marginTop: "10px" }}>
            <label>
              <input
                type="radio"
                name="deleteMode"
                value="SINGLE"
                checked={deleteMode === "SINGLE"}
                onChange={() => dispatch({ type: "setDeleteMode", payload: "SINGLE" })}
              />
              {" "}Delete only this occurrence
            </label>
            <br />
            <label>
              <input
                type="radio"
                name="deleteMode"
                value="ALL"
                checked={deleteMode === "ALL"}
                onChange={() => dispatch({ type: "setDeleteMode", payload: "ALL" })}
              />
              {" "}Delete entire event (all occurrences)
            </label>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

export default EventDetails;